import { BuilderBlock } from "@builder.io/sdk-react";

declare global {
  interface Window {
    navattic?: {
      identify: (payload: {
        campaignid?: string;
        googleanalyticsclientid?: string;
        googleclickid?: string;
        microsoftclickid?: string;
        siteid?: string;
        trackingcode?: string;
        utmcampaignmostrecent?: string;
        utmcontentmostrecent?: string;
        utmmediummostrecent?: string;
        utmsourcemostrecent?: string;
        utmtermmostrecent?: string;
        visitorguid?: string;
        sourceurl?: string;
        testcampaign?: string;
        testexperience?: string;
        pagetype?: string;
        majorversion?: string;
        devicetype?: string;
        externalreferrer?: string;
        siteparticipation?: string;
        siteentry?: string;
        entryurl?: string;
      }) => void;
      onEvent: (callback: (event: unknown) => void) => void;
    };
    __navatticCurrentCampaignId?: string;
    __navatticEventHandlerRegistered?: boolean;
  }
}

let lastIdentifiedNavatticCampaignId: string | undefined;

const getSatelliteCookie = (name: string) =>
  window._satellite?.cookie?.get?.(name);

const getABTestValuesFromCookies = () => {
  const experiments = getSatelliteCookie("SWI_AB_Test")?.split(";") ?? [];
  const latestExperiment = experiments[experiments.length - 1];

  return {
    testcampaign: latestExperiment?.split("|")[0] ?? undefined,
    testexperience: latestExperiment?.split("|")[1] ?? undefined,
  };
};

const getGCLIDCookie = () => {
  const GCLIDCookie = getSatelliteCookie("_gcl_aw");
  if (GCLIDCookie && GCLIDCookie.length > 0) {
    return GCLIDCookie.replace(/^GCL\.\d+\./, "");
  } else return GCLIDCookie;
};

/** Wait for Navattic to be defined, with timeout */
const waitForNavattic = (timeoutMs = 5000): Promise<boolean> => {
  return new Promise((resolve) => {
    if (window.navattic) {
      resolve(true);
      return;
    }

    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (window.navattic) {
        clearInterval(checkInterval);
        resolve(true);
        return;
      }

      if (Date.now() - startTime > timeoutMs) {
        clearInterval(checkInterval);
        console.warn(
          "Navattic failed to load after",
          timeoutMs,
          "ms. Proceeding without it.",
        );
        resolve(false);
      }
    }, 100);
  });
};

/** Wait for Satellite to be defined, with timeout */
const waitForSatellite = (timeoutMs = 5000): Promise<boolean> => {
  return new Promise((resolve) => {
    if (window._satellite) {
      resolve(true);
      return;
    }

    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (window._satellite) {
        clearInterval(checkInterval);
        resolve(true);
        return;
      }

      if (Date.now() - startTime > timeoutMs) {
        clearInterval(checkInterval);
        console.warn(
          "Satellite failed to load after",
          timeoutMs,
          "ms. Proceeding without it.",
        );
        resolve(false);
      }
    }, 100);
  });
};

type NavatticEventProperty = {
  step?: { indx: number; name: string; id: string };
  type: string;
};

const isStepOneEvent = (event: unknown) => {
  if (!event || typeof event !== "object") {
    return false;
  }

  const { step, type } = event as NavatticEventProperty;

  if (step && typeof step === "object") {
    if ((type === "ENGAGE" || type === "VIEW_STEP") && step.indx === 0) {
      return true;
    }
  }

  return false;
};

export const setNavatticCurrentCampaignId = (campaignId?: string) => {
  if (!campaignId) {
    return;
  }

  window.__navatticCurrentCampaignId = campaignId;
};

const identifyNavatticCampaign = (campaignId?: string) => {
  if (!campaignId || lastIdentifiedNavatticCampaignId === campaignId) {
    return;
  }

  console.log("Identifying Navattic with campaignId:", campaignId);

  window.__navatticCurrentCampaignId = campaignId;
  lastIdentifiedNavatticCampaignId = campaignId;
  window.navattic?.identify({
    campaignid: campaignId,
  });
};

export const identifyNavatticInitialProperties = async (
  campaignId?: string,
) => {
  await Promise.all([waitForNavattic(), waitForSatellite()]);
  window.__navatticCurrentCampaignId = campaignId;
  lastIdentifiedNavatticCampaignId = campaignId;
  console.log("Identifying Navattic with all:", {
    campaignid: campaignId,
    googleanalyticsclientid: getSatelliteCookie("_ga"),
    googleclickid: getGCLIDCookie(),
    microsoftclickid: getSatelliteCookie("_uetmsclkid"),
    trackingcode: getSatelliteCookie("SWI_Referral"),
    siteid: window.dataLayer.site.id,
    utmcampaignmostrecent: getSatelliteCookie("SWI_UTM_campaign_most_recent"),
    utmcontentmostrecent: getSatelliteCookie("SWI_UTM_content_most_recent"),
    utmmediummostrecent: getSatelliteCookie("SWI_UTM_medium_most_recent"),
    utmsourcemostrecent: getSatelliteCookie("SWI_UTM_source_most_recent"),
    utmtermmostrecent: getSatelliteCookie("SWI_UTM_term_most_recent"),
    visitorguid: getSatelliteCookie("SWI_SiteCatalyst_ID"),
    sourceurl: window.location.href,
    pagetype: window.dataLayer?.page?.type ?? undefined,
    majorversion: window.dataLayer?.site?.majorVersion,
    devicetype: window.dataLayer?.site?.type,
    externalreferrer: getSatelliteCookie("SWI_RefUrl"),
    siteparticipation: getSatelliteCookie("SWI_SiteParticipation"),
    siteentry: getSatelliteCookie("SWI_EntrySite"),
    entryurl: getSatelliteCookie("SWI_EntryURL"),
    ...getABTestValuesFromCookies(),
  });
  window.navattic?.identify({
    campaignid: campaignId,
    googleanalyticsclientid: getSatelliteCookie("_ga"),
    googleclickid: getGCLIDCookie(),
    microsoftclickid: getSatelliteCookie("_uetmsclkid"),
    trackingcode: getSatelliteCookie("SWI_Referral"),
    siteid: window.dataLayer.site.id,
    utmcampaignmostrecent: getSatelliteCookie("SWI_UTM_campaign_most_recent"),
    utmcontentmostrecent: getSatelliteCookie("SWI_UTM_content_most_recent"),
    utmmediummostrecent: getSatelliteCookie("SWI_UTM_medium_most_recent"),
    utmsourcemostrecent: getSatelliteCookie("SWI_UTM_source_most_recent"),
    utmtermmostrecent: getSatelliteCookie("SWI_UTM_term_most_recent"),
    visitorguid: getSatelliteCookie("SWI_SiteCatalyst_ID"),
    sourceurl: window.location.href,
    pagetype: window.dataLayer?.page?.type ?? undefined,
    majorversion: window.dataLayer?.site?.majorVersion,
    devicetype: window.dataLayer?.site?.type,
    externalreferrer: getSatelliteCookie("SWI_RefUrl"),
    siteparticipation: getSatelliteCookie("SWI_SiteParticipation"),
    siteentry: getSatelliteCookie("SWI_EntrySite"),
    entryurl: getSatelliteCookie("SWI_EntryURL"),
    ...getABTestValuesFromCookies(),
  });
};

/**
 * Get the campaign ID from the currently focused/active element =>
 * which should be iframe we interact with
 * */
const getActiveDemoIframeCampaignId = (): string | undefined => {
  const activeElement = document.activeElement;

  if (activeElement instanceof HTMLElement) {
    const id = activeElement.closest(
      "[data-demo-campaign-id]",
    ) as HTMLElement | null;

    if (id) {
      return id.getAttribute("data-demo-campaign-id") || undefined;
    }
  }

  return window.__navatticCurrentCampaignId;
};

export const registerNavatticEventHandler = async () => {
  if (window.__navatticEventHandlerRegistered) {
    return;
  }

  await waitForNavattic();

  if (!window.navattic) {
    return;
  }

  window.__navatticEventHandlerRegistered = true;
  window.navattic.onEvent((event) => {
    if (!isStepOneEvent(event)) {
      return;
    }

    const activeCampaignId = getActiveDemoIframeCampaignId();
    identifyNavatticCampaign(activeCampaignId);
  });
};

/**
 * content block helpers section to find demo and first campaign Id
 */
type BuilderColumn = {
  blocks?: BuilderBlock[];
};

export const hasNavattic = (blocks?: BuilderBlock[]): boolean => {
  return hasNavatticComponent(blocks) || hasDemoIframe(blocks);
};

// after removal of iframe demo logic should be moved to hasNavattic
const hasNavatticComponent = (blocks?: BuilderBlock[]): boolean => {
  if (!Array.isArray(blocks) || blocks.length === 0) return false;

  for (const block of blocks) {
    if (block?.component?.name === "Navattic") {
      return true;
    }

    if (block.children && Array.isArray(block.children)) {
      if (hasNavattic(block.children)) return true;
    }

    const columns = block?.component?.options?.columns as
      BuilderColumn[] | undefined;

    if (Array.isArray(columns)) {
      for (const column of columns) {
        if (hasNavattic(column.blocks)) return true;
      }
    }
  }

  return false;
};

// will be removed after removal of iframe demo logic
const hasDemoIframe = (blocks?: BuilderBlock[]): boolean => {
  if (!Array.isArray(blocks) || blocks.length === 0) return false;

  for (const block of blocks) {
    if (
      block?.component?.name === "Iframe Embed Code" &&
      block?.component?.options?.iframeContentType === "demo"
    ) {
      return true;
    }

    if (block.children && Array.isArray(block.children)) {
      if (hasDemoIframe(block.children)) return true;
    }

    const columns = block?.component?.options?.columns as
      BuilderColumn[] | undefined;

    if (Array.isArray(columns)) {
      for (const column of columns) {
        if (hasDemoIframe(column.blocks)) return true;
      }
    }
  }

  return false;
};

/** Get the first demo iframe campaign ID from Builder blocks */
export const getNavatticCampaignId = (
  blocks?: BuilderBlock[],
): string | undefined => {
  if (!Array.isArray(blocks) || blocks.length === 0) return undefined;

  for (const block of blocks) {
    if (
      block?.component?.name === "Navattic" ||
      (block?.component?.name === "Iframe Embed Code" &&
        block?.component?.options?.iframeContentType === "demo")
    ) {
      const campaignId = block?.component?.options?.campaignId;
      if (campaignId && typeof campaignId === "string") {
        return campaignId;
      }
    }

    if (block.children && Array.isArray(block.children)) {
      const result = getNavatticCampaignId(block.children);
      if (result) return result;
    }

    const columns = block?.component?.options?.columns as
      BuilderColumn[] | undefined;

    if (Array.isArray(columns)) {
      for (const column of columns) {
        const result = getNavatticCampaignId(column.blocks);
        if (result) return result;
      }
    }
  }

  return undefined;
};
